import { useState, useRef, useCallback, useEffect } from 'react';

const clone = (o) => JSON.parse(JSON.stringify(o));

const newSessionId = () =>
  (typeof crypto !== 'undefined' && crypto.randomUUID)
    ? crypto.randomUUID()
    : `s_${Date.now()}_${Math.floor(Math.random() * 1e9)}`;

/**
 * Shared autosave + undo/redo engine for editors.
 *
 * @param {object}   cfg
 * @param {object}   cfg.initialDocument  document to edit
 * @param {function} cfg.save             (doc, { sessionId }) => Promise — persists the doc
 * @param {number}   [cfg.debounceMs=1500]
 * @param {number}   [cfg.maxHistory=100]
 * @returns {{ document, update, undo, redo, canUndo, canRedo, status, flush, sessionId }}
 */
export function useAutosaveEditor({ initialDocument, save, debounceMs = 1500, maxHistory = 100 }) {
  const [present, setPresent] = useState(() => clone(initialDocument));
  const [past, setPast] = useState([]);
  const [future, setFuture] = useState([]);
  const [status, setStatus] = useState('idle'); // idle | dirty | saving | saved | error

  const sessionIdRef = useRef(newSessionId());
  const presentRef = useRef(present);
  presentRef.current = present;
  const dirtyRef = useRef(false);
  const timerRef = useRef(null);
  const retryRef = useRef(0);
  const saveRef = useRef(save);
  saveRef.current = save;
  const runSaveRef = useRef(null);

  const runSave = useCallback(async () => {
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    if (!dirtyRef.current) return;
    setStatus('saving');
    const docToSave = presentRef.current;
    try {
      await saveRef.current(docToSave, { sessionId: sessionIdRef.current });
      dirtyRef.current = false;
      retryRef.current = 0;
      setStatus('saved');
    } catch (e) {
      retryRef.current += 1;
      setStatus('error');
      const backoff = Math.min(30000, 1000 * 2 ** (retryRef.current - 1));
      timerRef.current = setTimeout(() => runSaveRef.current && runSaveRef.current(), backoff);
    }
  }, []);
  runSaveRef.current = runSave;

  const scheduleSave = useCallback(() => {
    dirtyRef.current = true;
    setStatus('dirty');
    retryRef.current = 0;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(runSave, debounceMs);
  }, [runSave, debounceMs]);

  const update = useCallback((updater) => {
    const prev = presentRef.current;
    // Function updater mutates the draft in place; its return value is ignored.
    const next = typeof updater === 'function'
      ? (() => { const c = clone(prev); updater(c); return c; })()
      : clone(updater);
    setPast((p) => [...p, prev].slice(-maxHistory));
    setFuture([]);
    setPresent(next);
    scheduleSave();
  }, [scheduleSave, maxHistory]);

  const undo = useCallback(() => {
    if (past.length === 0) return;
    setFuture((f) => [presentRef.current, ...f]);
    setPresent(past[past.length - 1]);
    setPast((p) => p.slice(0, -1));
    scheduleSave();
  }, [scheduleSave, past]);

  const redo = useCallback(() => {
    if (future.length === 0) return;
    setPast((p) => [...p, presentRef.current]);
    setPresent(future[0]);
    setFuture((f) => f.slice(1));
    scheduleSave();
  }, [scheduleSave, future]);

  const flush = useCallback(() => runSave(), [runSave]);

  // Warn before leaving while a save is pending or in-flight.
  useEffect(() => {
    const dirty = status === 'dirty' || status === 'saving' || status === 'error';
    if (!dirty) return undefined;
    const handler = (e) => { e.preventDefault(); e.returnValue = ''; return ''; };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [status]);

  // Clean up timer on unmount.
  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);

  return {
    document: present,
    update,
    undo,
    redo,
    canUndo: past.length > 0,
    canRedo: future.length > 0,
    status,
    flush,
    sessionId: sessionIdRef.current,
  };
}
